/* eslint-disable react/display-name */
import React from 'react';
import axios from 'helpers/axios';
const { format } = require('date-fns');

import { DataTable, DateRangePicker } from 'ui/components/shared';
import { useToasts, useMutableRef, useBlobDownload } from 'hooks';
import { Container } from 'ui/components/styled';
import { DownloadButton } from '../../Reports/styled';

export const JobLogs = () => {
  const [data, setData] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [totalRows, setTotalRows] = React.useState(0);
  const [perPage, setPerPage] = React.useState(15);
  const { errorToast } = useToasts();
  const [currentPage, setCurrentPage] = useMutableRef(1);
  const [startDate, setStartDate] = React.useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 7);
    return date;
  });
  const [endDate, setEndDate] = React.useState(new Date());
  const [filter, setFilter] = React.useState('');

  const downloadLink = React.useMemo(() => {
    if (startDate && endDate) {
      const formattedStartDate = format(startDate, 'MM/dd/yyyy');
      const formattedEndDate = format(endDate, 'MM/dd/yyyy');
      return `/logs/jobs/export?$top=${perPage}&$skip=${0}&filter=${filter}&startDate=${formattedStartDate}&endDate=${formattedEndDate}`;
    }
  }, [startDate, endDate, perPage, filter]);

  const download = useBlobDownload(downloadLink);

  const columns = [
    {
      name: 'Date',
      sortable: true,
      cell: (row) => {
        return format(new Date(row.created_at), "MM/dd/yyyy 'at' h:mm:ss a");
      },
    },
    {
      name: 'User',
      selector: 'user',
      sortable: true,
    },
    {
      name: 'Action',
      selector: 'action',
      sortable: true,
    },
    {
      name: 'Company',
      selector: 'company',
      sortable: true,
    },
    {
      name: 'Monitor',
      selector: 'job',
      sortable: true,
    },
  ];

  const fetchJobsLogs = async (page) => {
    if (!startDate || !endDate) return;
    setLoading(true);
    try {
      const skip = page > 1 ? (page - 1) * perPage : 0;
      const formattedStartDate = format(startDate, 'MM/dd/yyyy');
      const formattedEndDate = format(endDate, 'MM/dd/yyyy');

      const response = await axios.get(
        `/logs/jobs?$top=${perPage}&$skip=${skip}&filter=${filter}&startDate=${formattedStartDate}&endDate=${formattedEndDate}`
      );

      if (response.status === 200) {
        setData(response.data.value);
        setTotalRows(response.data.count);
      }
    } catch (error) {
      errorToast({ error, default: 'Error: Fetch logs.' });
    }
    setLoading(false);
  };

  const handleRowsPerPageChange = async (newPerPage, page) => {
    setLoading(true);
    setCurrentPage(page);

    try {
      const skip = page > 1 ? (page - 1) * newPerPage : 0;
      const response = await axios.get(`/logs/jobs/?$top=${newPerPage}&$skip=${skip}`);

      if (response.status === 200) {
        setData(response.data.value);
        setPerPage(newPerPage);
      }
    } catch (error) {
      errorToast({ error, default: 'Error: Rows Change.' });
    }
    setLoading(false);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchJobsLogs(page);
  };

  const handleFilterChange = (newFilter) => {
    setFilter(newFilter);
  };

  React.useEffect(() => {
    fetchJobsLogs(1);
  }, [startDate, endDate, filter]);

  return (
    <DataTable
      columns={columns}
      data={data}
      progressPending={loading}
      paginationPerPage={perPage}
      paginationTotalRows={totalRows}
      paginationCurrentPage={currentPage.current}
      onRowsPerPageChange={handleRowsPerPageChange}
      onPageChange={handlePageChange}
      onPageReset={setCurrentPage}
      filterPlaceholder="Search"
      pointerOnHover
      onFilter={handleFilterChange}
      subHeaderActions={
        <Container flexDirection="row" alignItems="center" width="auto">
          <DownloadButton mr="m" onClick={download.onClick} disabled={false}>
            Download
          </DownloadButton>
          <DateRangePicker
            format="MM/dd/yyyy"
            startDate={startDate}
            endDate={endDate}
            setStartDate={setStartDate}
            setEndDate={setEndDate}
          />
        </Container>
      }
    />
  );
};
